import { inject } from '@angular/core';
import {
  FilesShareDialogDelegateI,
  FileToShare,
  ListOfBasicUsersInfo,
  PermissionViewModel,
  ShareFilesDialogComponent,
} from '@dntlab/share-files-dialog';
import { catchError, firstValueFrom, forkJoin, map, Observable, of } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { NotificationContextAttributesService } from 'src/app/notifications/notifications-context-attributes.service';
import { NotificationService } from 'src/app/notifications/notifications.service';
import {
  FileDescriptor,
  FilePermissions,
  ListOfBasicUsers,
  DefaultService as RestApiService,
  SecurityDescriptor,
} from '../../swagger-api/';
import { adaptListOfUsers, fileDescriptorToFileShareAdapter } from './files-share-dialog.helpers';

export class FilesShareDialogDelegate implements FilesShareDialogDelegateI {
  private apiService = inject(RestApiService);
  private sessionService = inject(SessionService);
  private notificationService = inject(NotificationService);
  private notificationContextService = inject(NotificationContextAttributesService);

  public getShareableLink(_dialog: ShareFilesDialogComponent, files: FileToShare[]): Promise<FileToShare[]> {
    const query: Observable<FileDescriptor>[] = [];
    files.forEach((fileToShare: FileToShare) => {
      query.push(this.apiService.restFileStorageFileShareableLinkPut(fileToShare.file.entityId));
    });
    return firstValueFrom(
      forkJoin(query).pipe(
        map((fileDescriptors: FileDescriptor[]) =>
          fileDescriptorToFileShareAdapter(fileDescriptors, this.sessionService.userId),
        ),
        catchError(() => {
          return of([]);
        }),
      ),
    );
  }

  public removeLink(_dialog: ShareFilesDialogComponent, files: FileToShare[]): Promise<FileToShare[]> {
    const query: Observable<FileDescriptor>[] = [];
    files.forEach((fileToShare: FileToShare) => {
      query.push(
        this.apiService.restFileStorageFileShareableLinkDelete(fileToShare.file.entityId) as Observable<FileDescriptor>,
      );
    });
    return firstValueFrom(
      forkJoin(query).pipe(
        map((response: FileDescriptor[]) => fileDescriptorToFileShareAdapter(response, this.sessionService.userId)),
        catchError(() => {
          return of([]);
        }),
      ),
    );
  }

  public shareFiles(_dialog: ShareFilesDialogComponent, filesToShare: FileToShare[]): Promise<FileToShare[]> {
    const query: Observable<FileToShare>[] = [];
    filesToShare.forEach((fileToShare: FileToShare) => {
      const requestBody: SecurityDescriptor[] = this.formateRequestBody(fileToShare.security);

      query.push(
        fileToShare.file.isDirectory
          ? (this.apiService.restFileStorageFolderSharingPut(
              { security_permission: requestBody },
              fileToShare.file.entityId,
            ) as Observable<FileToShare>)
          : (this.apiService.restFileStorageFileSharingPut(
              { security_permission: requestBody },
              fileToShare.file.entityId,
            ) as Observable<FileToShare>),
      );
    });

    return firstValueFrom(
      forkJoin(query).pipe(
        map(() => filesToShare),
        catchError(() => {
          return of([]);
        }),
      ),
    );
  }

  public getSharedUsers(_dialog: ShareFilesDialogComponent, userList: string[]): Promise<ListOfBasicUsersInfo[]> {
    const query: Observable<ListOfBasicUsers> = this.apiService.restUserIndex(userList);

    return firstValueFrom(
      forkJoin([query]).pipe(
        map((response: ListOfBasicUsers[]) => adaptListOfUsers(response)),
        catchError(() => {
          return of([]);
        }),
      ),
    );
  }

  public showModalWindow(): Observable<boolean> {
    return this.notificationService.dialogSaveData({
      title: 'EXIT_NOTIFICATION_TITLE',
      description: 'EXIT_NOTIFICATION_DESCRIPTION',
      buttons: this.notificationContextService.getExitButtons(),
    });
  }

  private formateRequestBody(associations: PermissionViewModel[]): SecurityDescriptor[] {
    const requestBody: SecurityDescriptor[] = [];
    associations.forEach((association: PermissionViewModel) => {
      const permission = `${association.permission.substring(0, 1).toUpperCase()}${association.permission.substring(
        1,
      )}`;
      requestBody.push({
        user_id: association.user.id,
        permission: FilePermissions[permission] as FilePermissions,
      });
    });
    return requestBody;
  }
}
